import React, {
  Fragment,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { compact, pick } from "lodash";

import { useCustomGoalsContext } from "@components/data/custom-goals-provider";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { useUser } from "@components/user-context";
import { useLogger, usePrettyLogEventPage } from "@components/v2/analytics";
import { useCustomMetrics } from "@features/custom-metrics/hooks/use-custom-metrics";
import { DataExportBuilderModal } from "@features/data-export";
import { useChartState } from "@features/sales-v3/hooks";
import { useShowV1Forecasts } from "@features/sales-v3/hooks/use-show-v1-forecasts";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { getSalesPageMetricSpecs } from "@north-beam/nb-client-side/metrics";
import { errors } from "@north-beam/nb-common";

import { useSalesPageV3NextContext } from "../sales-v3-next-provider";

export interface ExportMenuProps {
  onChartExport?: (params: { index: number }) => void;
}

export const ExportMenu = ({ onChartExport }: ExportMenuProps) => {
  const [exportModalOpened, setExportModalOpened] = useState(false);
  const { chartConfigs } = useChartState();

  const { user } = useUser();

  const {
    accountingMode,
    attributionModel,
    attributionWindow,
    breakdowns,
    dateRange,
    timeGranularity,
    activeTab,
  } = useSalesPageV3NextContext();

  const { customGoals } = useCustomGoalsContext();
  const { customMetricData } = useCustomMetrics();

  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const prettyLogEventPage = usePrettyLogEventPage();
  const logger = useLogger({ container: "data-export-menu" });

  const showV1Forecasts = useShowV1Forecasts(
    timeGranularity,
    attributionModel,
    accountingMode,
  );

  const dataExportBreakdowns = useMemo(() => {
    return compact(
      breakdowns.map((breakdown) => {
        const config = breakdownConfigs.find((c) => c.key === breakdown.key);

        return config
          ? { key: config.key, values: config.choices.map((e) => e.value) }
          : null;
      }),
    );
  }, [breakdowns, breakdownConfigs]);

  const metricSpecs = useMemo(() => {
    return getSalesPageMetricSpecs(
      user,
      showV1Forecasts,
      customGoals,
      customMetricData,
    );
  }, [customMetricData, customGoals, showV1Forecasts, user]);

  const onExportData = useCallback(
    (exportType: "table" | "chart", index: number) => {
      switch (exportType) {
        case "table":
          prettyLogEventPage({
            domain: "DataExport",
            action: "onClick",
            additionalActionInfo: "Data Export Modal opened",
          });
          setExportModalOpened(true);
          break;
        case "chart":
          logger.event.exportSalesChartData("click", {
            eventDetails: {
              accountingMode,
              attributionModel,
              attributionWindow,
              timeGranularity,
              dateRange:
                typeof dateRange === "string"
                  ? dateRange
                  : pick(dateRange, ["startDate", "endDate"]),
            },
          });
          prettyLogEventPage({
            domain: "DataExport",
            action: "onClick",
            additionalActionInfo: "Chart Export",
          });
          if (onChartExport) {
            onChartExport({ index });
          }
          break;
        default:
          throw new errors.UnreachableCaseError(exportType);
      }
    },
    [
      onChartExport,
      prettyLogEventPage,
      logger,
      accountingMode,
      attributionModel,
      dateRange,
      timeGranularity,
      attributionWindow,
    ],
  );

  return (
    <>
      <DataExportBuilderModal
        isOpen={exportModalOpened}
        target="performance-metrics"
        availableMetricSpecs={metricSpecs}
        // This is closely tied to the old table architecture,
        // we need to pull the selected metric IDs from somewhere.
        selectedMetricIds={[]}
        // selectedMetricIds={columns
        //   .filter((e) => e.metric && e.metric.spec)
        //   .map((e) => e.metric.spec!.id)}
        attributionModel={attributionModel}
        attributionWindow={attributionWindow}
        nbAccountingMode={accountingMode}
        granularity={activeTab === "breakdown" ? "platform" : activeTab}
        timeGranularity={timeGranularity}
        breakdowns={dataExportBreakdowns}
        dateRange={dateRange}
        onClose={() => {
          prettyLogEventPage({
            domain: "DataExport",
            action: "onClose",
            additionalActionInfo: "Data Export Modal closed",
          });
          setExportModalOpened(false);
        }}
      />
      <div className="position-relative">
        <Menu>
          {({ open }) => (
            <>
              <MenuButton
                as="button"
                className={clsx("button secondary-button sm font-bold", {
                  "text-white bg-cosmic border-cosmic": open,
                })}
              >
                <span className="mr-2">EXPORT</span>
                <motion.i
                  initial={false}
                  animate={{ rotate: open ? 0 : 180 }}
                  transition={{ duration: 0.2 }}
                  className={clsx("fa-regular fa-sharp fa-chevron-up", {
                    "text-white": open,
                  })}
                />
              </MenuButton>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <MenuItems
                  className={clsx(
                    "absolute z-10 right-0 bg-white rounded-lg elevation-1",
                    "text-sm pt-1 pb-2 w-[160px]",
                  )}
                >
                  {chartConfigs.map((_, i) => (
                    <Option
                      key={i}
                      onClick={() => {
                        onExportData("chart", i);
                      }}
                    >
                      <i className="fa-regular fa-sharp fa-chart-line text-primary mr-2" />
                      Chart {chartConfigs.length > 1 && <>{i + 1}</>} values
                    </Option>
                  ))}
                  <Option
                    onClick={() => {
                      onExportData("table", 0);
                    }}
                  >
                    <i className="fa-regular fa-sharp fa-table text-primary mr-2" />
                    Table values
                  </Option>
                </MenuItems>
              </Transition>
            </>
          )}
        </Menu>
      </div>
    </>
  );
};

export const ExportTableModal = ({
  exportModalOpened,
  setExportModalOpened,
}: {
  exportModalOpened: boolean;
  setExportModalOpened: (open: boolean) => void;
}) => {
  const { user } = useUser();

  const {
    accountingMode,
    attributionModel,
    attributionWindow,
    breakdowns,
    dateRange,
    timeGranularity,
    activeTab,
  } = useSalesPageV3NextContext();

  const { customGoals } = useCustomGoalsContext();
  const { customMetricData } = useCustomMetrics();

  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const prettyLogEventPage = usePrettyLogEventPage();

  const showV1Forecasts = useShowV1Forecasts(
    timeGranularity,
    attributionModel,
    accountingMode,
  );

  const dataExportBreakdowns = useMemo(() => {
    return compact(
      breakdowns.map((breakdown) => {
        const config = breakdownConfigs.find((c) => c.key === breakdown.key);

        return config
          ? { key: config.key, values: config.choices.map((e) => e.value) }
          : null;
      }),
    );
  }, [breakdowns, breakdownConfigs]);

  const metricSpecs = useMemo(() => {
    return getSalesPageMetricSpecs(
      user,
      showV1Forecasts,
      customGoals,
      customMetricData,
    );
  }, [customMetricData, customGoals, showV1Forecasts, user]);

  return (
    <>
      <DataExportBuilderModal
        isOpen={exportModalOpened}
        target="performance-metrics"
        availableMetricSpecs={metricSpecs}
        // This is closely tied to the old table architecture,
        // we need to pull the selected metric IDs from somewhere.
        selectedMetricIds={[]}
        // selectedMetricIds={columns
        //   .filter((e) => e.metric && e.metric.spec)
        //   .map((e) => e.metric.spec!.id)}
        attributionModel={attributionModel}
        attributionWindow={attributionWindow}
        nbAccountingMode={accountingMode}
        granularity={activeTab === "breakdown" ? "platform" : activeTab}
        timeGranularity={timeGranularity}
        breakdowns={dataExportBreakdowns}
        dateRange={dateRange}
        onClose={() => {
          prettyLogEventPage({
            domain: "DataExport",
            action: "onClose",
            additionalActionInfo: "Data Export Modal closed",
          });
          setExportModalOpened(false);
        }}
      />
    </>
  );
};

const Option = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick(): void;
}) => (
  <MenuItem as={Fragment}>
    {({ focus }) => {
      return (
        <div
          className={clsx(
            "w-full text-left px-4 hover:bg-cosmic-4 h-8",
            "flex items-center cursor-pointer",
            {
              "bg-cosmic-4": focus,
            },
          )}
          onClick={onClick}
        >
          {children}
        </div>
      );
    }}
  </MenuItem>
);
